import md5 from 'md5';
import queryString from 'query-string';
import { useEffect, useReducer } from 'react';

import 'cross-fetch/polyfill';

const imageSizes = {
  xsmall: 20,
  small: 26,
  medium: 48,
  large: 120,
  xlarge: 160,
};

export type AvatarSize = keyof typeof imageSizes;

const getGravatarUrl = (email: string, size: AvatarSize) => {
  const hash = md5(email.trim().toLowerCase());
  const params = queryString.stringify({
    d: '404',
    r: 'g', // Use G-rated images
    s: imageSizes[size], // Specify image size
  });

  return `https://www.gravatar.com/avatar/${hash}?${params}`;
};

type AvatarAction =
  | {
      type: 'URL_VERIFIED';
      url: string;
    }
  | {
      type: 'URL_FAILED';
    }
  | {
      type: 'NO_EMAIL_PROVIDED';
    };

interface AvatarState {
  imageType: 'empty' | 'svg' | 'identicon' | 'gravatar';
  imageUrl: string;
}

const reducer = (state: AvatarState, action: AvatarAction): AvatarState => {
  switch (action.type) {
    case 'NO_EMAIL_PROVIDED':
      return {
        ...state,
        imageType: 'svg',
      };
    case 'URL_FAILED':
      return {
        ...state,
        imageType: 'identicon',
      };
    case 'URL_VERIFIED':
      return {
        ...state,
        imageType: 'gravatar',
        imageUrl: action.url,
      };
  }
};

export const useAvatarImage = (email: string, size: AvatarSize) => {
  const [{ imageType, imageUrl }, dispatch] = useReducer(reducer, {
    imageType: 'empty',
    imageUrl: '',
  });

  useEffect(() => {
    if (!email) {
      dispatch({ type: 'NO_EMAIL_PROVIDED' });
      return;
    }

    const url = getGravatarUrl(email, size);
    const controller = new AbortController();
    // eslint-disable-next-line no-restricted-globals
    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (response.ok) {
          dispatch({ type: 'URL_VERIFIED', url });
        } else {
          dispatch({ type: 'URL_FAILED' });
        }
      })
      .catch((e) => {
        // An "AbortError" is thrown when the controller cancels the request We
        // cancel requests in effects cleanup function below, which is triggered
        // either when the effect is re-triggered or the component is about to
        // unmount In either case, we want to ignore the result
        if (e.name !== 'AbortError') {
          dispatch({ type: 'URL_FAILED' });
        }
      });

    return () => {
      controller.abort();
    };
  }, [email, size]);

  return {
    imageType,
    imageUrl,
    dim: imageSizes[size],
  };
};
